/**
 * DatePicker — AWLP Design System
 * A calendar popover date picker (replaces the native <input type="date">).
 * Pattern adapted from the shadcn/21st.dev calendar into styled-components +
 * theme tokens (no Tailwind). Value is an ISO date string 'YYYY-MM-DD'.
 *
 * Dates are parsed/formatted in LOCAL time (not UTC) to avoid off-by-one-day
 * shifts that `new Date('YYYY-MM-DD')` would introduce.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  MONTHS,
  WEEKDAYS,
  parseISO,
  sameDay,
  toISO,
  useClosePopoverOnOutsideOrEscape,
  useMonthGrid,
} from './datePickerShared';
import { Empty, Foot, FootBtn, Grid, Head, HeadLabel, NavBtn, WeekCell, WeekRow, Wrap } from './datePickerStyles';

export interface DatePickerProps {
  value?: string; // 'YYYY-MM-DD'
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Earliest selectable date (ISO 'YYYY-MM-DD'). Days before this are rendered disabled. */
  min?: string;
}

// Estimated popover height (incl. the 6px gap) used to decide flip direction.
const POPOVER_HEIGHT_ESTIMATE = 340;
const POPOVER_WIDTH = 280;

function formatLong(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function DatePicker({ value, onChange, disabled, placeholder, min }: DatePickerProps) {
  const selected = parseISO(value);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; ready: boolean }>({
    top: 0,
    left: 0,
    ready: false,
  });
  const { view, setView, cells, prevMonth, nextMonth } = useMonthGrid(selected ?? new Date());
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  const minDate = parseISO(min);

  // Keep the visible month in sync when the bound value changes externally.
  useEffect(() => {
    const next = parseISO(value);
    if (next) setView(next);
  }, [value, setView]);

  // The calendar renders in a body-level portal (position: fixed) so it can
  // never be clipped by a scrollable/overflow-hidden ancestor (e.g. a Modal
  // with max-height + overflow-y: auto). We compute its viewport coords from
  // the trigger, flipping upward when there isn't room below, and reposition
  // on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;

    const reposition = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropUp = spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;
      const top = dropUp
        ? Math.max(8, rect.top - POPOVER_HEIGHT_ESTIMATE - 6)
        : rect.bottom + 6;
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - POPOVER_WIDTH - 8),
      );
      setCoords({ top, left, ready: true });
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useClosePopoverOnOutsideOrEscape(open, wrapRef, () => setOpen(false), popRef);

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        disabled={disabled}
        $hasValue={!!selected}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarIcon size={15} />
        <span>{selected ? formatLong(selected) : placeholder || 'Select a date'}</span>
      </Trigger>

      {open && !disabled && createPortal(
        <Pop ref={popRef} style={{ top: coords.top, left: coords.left, visibility: coords.ready ? 'visible' : 'hidden' }}>
          <Head>
            <NavBtn type="button" onClick={prevMonth} aria-label="Previous month">
              <ChevronLeft size={16} />
            </NavBtn>
            <HeadLabel>
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </HeadLabel>
            <NavBtn type="button" onClick={nextMonth} aria-label="Next month">
              <ChevronRight size={16} />
            </NavBtn>
          </Head>

          <WeekRow>
            {WEEKDAYS.map((w) => (
              <WeekCell key={w}>{w}</WeekCell>
            ))}
          </WeekRow>

          <Grid>
            {cells.map((d, i) => {
              if (d === null) return <Empty key={`e${i}`} />;
              const iso = toISO(d);
              const beforeMin = !!minDate && d < minDate;
              return (
                <Day
                  key={iso}
                  type="button"
                  $selected={!!selected && sameDay(d, selected)}
                  $today={sameDay(d, today)}
                  $disabled={beforeMin}
                  disabled={beforeMin}
                  onClick={() => {
                    if (beforeMin) return;
                    onChange(iso);
                    setOpen(false);
                  }}
                >
                  {d.getDate()}
                </Day>
              );
            })}
          </Grid>

          <Foot>
            <FootBtn
              type="button"
              disabled={!!minDate && today < minDate}
              onClick={() => {
                const t = new Date();
                onChange(toISO(t));
                setView(t);
                setOpen(false);
              }}
            >
              Today
            </FootBtn>
            {selected && (
              <FootBtn
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                Clear
              </FootBtn>
            )}
          </Foot>
        </Pop>,
        document.body,
      )}
    </Wrap>
  );
}

export default DatePicker;

const Trigger = styled.button<{ $hasValue: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ $hasValue, theme }) =>
    $hasValue ? theme.colors.text.primary : theme.colors.text.light};
  text-align: left;
  cursor: pointer;

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    cursor: not-allowed;
  }
`;

const Pop = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.tooltip};
  width: ${POPOVER_WIDTH}px;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const Day = styled.button<{ $selected: boolean; $today: boolean; $disabled?: boolean }>`
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ $selected, $disabled, theme }) =>
    $disabled
      ? theme.colors.text.light
      : $selected
        ? theme.colors.text.white
        : theme.colors.text.primary};
  background: ${({ $selected, $disabled, theme }) =>
    $selected && !$disabled ? theme.colors.primary.base : 'transparent'};
  border: 1px solid
    ${({ $today, $selected, $disabled, theme }) =>
      $today && !$selected && !$disabled ? theme.colors.primary.base : 'transparent'};
  font-weight: ${({ $selected, $today, theme }) =>
    $selected || $today ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  &:hover:not(:disabled) {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.primary.dark : theme.colors.backgrounds.light};
  }
`;

